import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { MouseEvent } from 'react'
import type { PnlDay } from '../pnl'

/**
 * PnL Takip · aylık görünümün sağ sütunu: günlük PnL sütun grafiği (üstte) ve
 * kümülatif PnL çizgisi (altta). Kullanıcı isteği 2026-08-02.
 *
 * İkisi de AYNI gün dizisini ve AYNI x ölçeğini kullanır (ayın tamamı, boş
 * günler dahil) — üst üste duran iki panelin sütunu ve noktası dikeyde
 * hizalanır, göz birinden diğerine kayarken gün aramak zorunda kalmaz. Bu
 * yüzden "boş günler" anahtarı yalnız TABLOYU süzer: grafikte 3 Ağustos'un
 * yerinin boş kalması, hiç var olmamasından daha çok şey anlatır.
 *
 * Vurgu (`vurgu`/`onVurgu`) sayfada ORTAK: tabloda satırın üstüne gelmek
 * grafiklerdeki günü, grafikte gezinmek tablodaki satırı işaretler.
 *
 * Grafik dili tahtadaki TradeChart ile aynı: ızgara kesintisiz hairline,
 * eksen yazıları `.chart-tick`, kutu `.chart-tooltip`, ölçü ResizeObserver +
 * her render ölçümü (panel arka planda mount olduğunda 0 ölçüsüne takılmasın).
 */

const PAD = { top: 14, right: 12, bottom: 22, left: 58 }
/** Sütun kalınlığı tavanı — 28 günlük ayda sütunlar kalın bloklara dönmesin. */
const MAX_BAR = 22
const BAR_GAP = 3

const tlFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
/** Eksen etiketi: 12.480 → "12,5 B". Uzun sayı ekseni kalabalıklaştırıyor. */
const kisaFmt = new Intl.NumberFormat('tr-TR', {
  notation: 'compact',
  maximumFractionDigits: 1,
})
const HAFTA_ADI = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt']

function gunNo(iso: string): number {
  return Number(iso.slice(8, 10))
}

/** "02.08.2026 · Paz" — kutunun başlık satırı. */
function tamTarih(iso: string): string {
  const yil = Number(iso.slice(0, 4))
  const ay = Number(iso.slice(5, 7))
  const gun = Number(iso.slice(8, 10))
  const ad = HAFTA_ADI[new Date(yil, ay - 1, gun).getDay()] ?? ''
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)} · ${ad}`
}

/** Yuvarlak sayılara oturan eksen değerleri (1 / 2 / 2,5 / 5 katları). */
function tickler(min: number, max: number, count: number): number[] {
  const raw = (max - min) / count
  if (!Number.isFinite(raw) || raw <= 0) return [0]
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? magnitude * 10
  const out: number[] = []
  for (let v = Math.ceil(min / step) * step; v <= max + step * 1e-6; v += step) {
    out.push(Math.abs(v) < step * 1e-6 ? 0 : v)
  }
  return out
}

/** Panel esnek yükseklikte; SVG gerçek piksel ölçüsüyle çizilir. */
function useOlcu() {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  const olc = () => {
    const el = ref.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    setSize((p) => (p.width === width && p.height === height ? p : { width, height }))
  }
  useLayoutEffect(olc)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const gozlemci = new ResizeObserver(olc)
    gozlemci.observe(el)
    return () => gozlemci.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return { ref, size }
}

/** Ortak x ekseni: gün başına eşit bant + seyrekleştirilmiş gün numaraları. */
function xEkseni(n: number, plotWidth: number) {
  const band = plotWidth / Math.max(1, n)
  const x = (i: number) => PAD.left + band * (i + 0.5)
  // Etiketler en az 26px arayla; 31 günde her gün sığmaz, 1/5/10… olur.
  const adim = Math.max(1, Math.ceil(26 / band))
  return { band, x, adim }
}

interface Props {
  /** Ayın TÜM günleri (sunucu her günü döndürür) — sıra = takvim sırası. */
  gunler: PnlDay[]
  /** Sayfa geneli vurgulanan gün (dizin) — tabloyla ortak. */
  vurgu: number | null
  onVurgu: (i: number | null) => void
  /** Güne tıklanınca günlük görünüme in. */
  onGun: (i: number) => void
}

/** Günün PnL'i — dolumu olmayan gün grafikte SIFIR değil, YOK sayılır. */
function deger(g: PnlDay): number | null {
  return g.total.buy.mwh > 0 || g.total.sell.mwh > 0 ? g.total.pnlTl : null
}

/* ── Panel 1: günlük PnL sütunları ────────────────────────────────────────── */

export function PnlBarChart({ gunler, vurgu, onVurgu, onGun }: Props) {
  const { ref, size } = useOlcu()
  const [kutu, setKutu] = useState(false) // fare bu panelde mi (tooltip sahibi)

  const yer = useMemo(() => {
    const { width, height } = size
    if (width < 160 || height < 90 || gunler.length === 0) return null
    const plotWidth = width - PAD.left - PAD.right
    const plotHeight = height - PAD.top - PAD.bottom
    const { band, x, adim } = xEkseni(gunler.length, plotWidth)

    const degerler = gunler.map(deger)
    const dolu = degerler.filter((v): v is number => v != null)
    // Sıfır ekseni HER ZAMAN ölçekte: kâr/zarar sütunları ortak tabandan
    // okunmazsa yön anlamını yitirir.
    let vMin = Math.min(0, ...dolu)
    let vMax = Math.max(0, ...dolu)
    if (vMin === vMax) {                    // hiç veri yok ya da hepsi sıfır
      vMin = -1
      vMax = 1
    }
    const pay = (vMax - vMin) * 0.12
    vMin -= pay
    vMax += pay
    const y = (v: number) => PAD.top + (1 - (v - vMin) / (vMax - vMin)) * plotHeight
    const sifir = y(0)
    const kalinlik = Math.max(2, Math.min(MAX_BAR, band - BAR_GAP))

    const sutunlar = degerler.map((v, i) => {
      if (v == null || v === 0) return null
      const yv = y(v)
      const yukari = v > 0
      return {
        i,
        v,
        yukari,
        x: x(i) - kalinlik / 2,
        y: yukari ? yv : sifir,
        h: Math.max(1.5, Math.abs(sifir - yv)),
        w: kalinlik,
      }
    })

    return {
      band, x, adim, y, sifir, plotWidth, plotHeight, sutunlar,
      ticks: tickler(vMin, vMax, 4),
    }
  }, [size, gunler])

  const vurgulu = vurgu != null ? gunler[vurgu] : undefined

  return (
    <div className="panel chart-panel pnl-grafik">
      <div className="panel-title">
        Günlük PnL
        <span className="panel-meta">TL</span>
      </div>
      <div className="chart-box" ref={ref}>
        {!yer ? (
          <div className="chart-empty">Çiziliyor…</div>
        ) : (
          <svg
            className="chart-svg"
            viewBox={`0 0 ${size.width} ${size.height}`}
            preserveAspectRatio="none"
            onMouseMove={(e: MouseEvent<SVGSVGElement>) => {
              const rect = e.currentTarget.getBoundingClientRect()
              const i = Math.floor((e.clientX - rect.left - PAD.left) / yer.band)
              setKutu(true)
              onVurgu(Math.min(Math.max(i, 0), gunler.length - 1))
            }}
            onMouseLeave={() => {
              setKutu(false)
              onVurgu(null)
            }}
            onClick={() => vurgu != null && onGun(vurgu)}
            role="img"
            aria-label="Günlük PnL sütun grafiği"
          >
            {yer.ticks.map((t) => (
              <g key={t}>
                <line
                  className={t === 0 ? 'chart-zero' : 'chart-grid'}
                  x1={PAD.left}
                  x2={PAD.left + yer.plotWidth}
                  y1={yer.y(t)}
                  y2={yer.y(t)}
                />
                <text className="chart-tick" x={PAD.left - 8} y={yer.y(t) + 3.5}>
                  {t === 0 ? '0' : kisaFmt.format(t)}
                </text>
              </g>
            ))}

            {/* Vurgulanan günün bandı — sütunun kendisi değil ARKASI boyanır ki
                rengi (kâr/zarar) bozulmasın. */}
            {vurgu != null && (
              <rect
                className="chart-band"
                x={yer.x(vurgu) - yer.band / 2}
                y={PAD.top}
                width={yer.band}
                height={yer.plotHeight}
              />
            )}

            {yer.sutunlar.map((s) =>
              s == null ? null : (
                <path
                  key={s.i}
                  className={`pnl-sutun ${s.yukari ? 'poz' : 'neg'}${vurgu === s.i ? ' vurgulu' : ''}`}
                  d={sutunPath(s.x, s.y, s.w, s.h, s.yukari)}
                />
              ),
            )}

            {Array.from({ length: gunler.length }, (_, i) => i)
              .filter((i) => i % yer.adim === 0)
              .map((i) => (
                <text
                  key={i}
                  className="chart-tick chart-tick-x"
                  x={yer.x(i)}
                  y={size.height - 6}
                >
                  {gunNo(gunler[i]!.date)}
                </text>
              ))}
          </svg>
        )}

        {kutu && vurgulu && yer && (
          <div
            className="chart-tooltip"
            style={{
              left: Math.min(Math.max(yer.x(vurgu!) + 12, 8), Math.max(size.width - 152, 8)),
              top: 10,
            }}
          >
            <div className="tooltip-time">{tamTarih(vurgulu.date)}</div>
            <div className="tooltip-row">
              <span>PnL</span>
              <b className={renk(deger(vurgulu))}>{fmt(deger(vurgulu))}</b>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* ── Panel 2: kümülatif PnL çizgisi ───────────────────────────────────────── */

export function PnlCumulativeChart({ gunler, vurgu, onVurgu, onGun }: Props) {
  const { ref, size } = useOlcu()
  const [kutu, setKutu] = useState(false)

  const yer = useMemo(() => {
    const { width, height } = size
    if (width < 160 || height < 90 || gunler.length === 0) return null
    const plotWidth = width - PAD.left - PAD.right
    const plotHeight = height - PAD.top - PAD.bottom
    const { band, x, adim } = xEkseni(gunler.length, plotWidth)

    // Kümülatif toplam AYIN TAMAMI üzerinden yürür; işlemsiz gün çizgiyi
    // düz taşır (sıfırlamaz) — birikmiş sonuç o gün de aynen duruyor.
    let toplam = 0
    const seri = gunler.map((g) => {
      const v = deger(g)
      if (v != null) toplam += v
      return toplam
    })

    let vMin = Math.min(0, ...seri)
    let vMax = Math.max(0, ...seri)
    if (vMin === vMax) {
      vMin = -1
      vMax = 1
    }
    const pay = (vMax - vMin) * 0.12
    vMin -= pay
    vMax += pay
    const y = (v: number) => PAD.top + (1 - (v - vMin) / (vMax - vMin)) * plotHeight
    const sifir = y(0)

    const noktalar = seri.map((v, i) => ({ i, v, x: x(i), y: y(v) }))
    const cizgi = noktalar.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ')
    const ilk = noktalar[0]!
    const son = noktalar[noktalar.length - 1]!
    // Alan sıfır ekseninden taranır: kümülatif eksideyken dolgu da aşağı taşar,
    // "ne kadar altındayız" bakışta okunur.
    const alan = `${cizgi} L${son.x} ${sifir} L${ilk.x} ${sifir} Z`

    return {
      band, x, adim, y, sifir, plotWidth, plotHeight, seri, noktalar, cizgi, alan,
      son, ticks: tickler(vMin, vMax, 4),
      artida: son.v >= 0,
    }
  }, [size, gunler])

  const vurgulu = vurgu != null ? gunler[vurgu] : undefined
  const nokta = yer && vurgu != null ? yer.noktalar[vurgu] : undefined

  return (
    <div className="panel chart-panel pnl-grafik">
      <div className="panel-title">
        Kümülatif PnL
        <span className={`panel-meta${yer ? renk(yer.son.v) : ''}`}>
          {yer ? `${fmt(yer.son.v)} TL` : 'TL'}
        </span>
      </div>
      <div className="chart-box" ref={ref}>
        {!yer ? (
          <div className="chart-empty">Çiziliyor…</div>
        ) : (
          <svg
            className="chart-svg"
            viewBox={`0 0 ${size.width} ${size.height}`}
            preserveAspectRatio="none"
            onMouseMove={(e: MouseEvent<SVGSVGElement>) => {
              const rect = e.currentTarget.getBoundingClientRect()
              const i = Math.floor((e.clientX - rect.left - PAD.left) / yer.band)
              setKutu(true)
              onVurgu(Math.min(Math.max(i, 0), gunler.length - 1))
            }}
            onMouseLeave={() => {
              setKutu(false)
              onVurgu(null)
            }}
            onClick={() => vurgu != null && onGun(vurgu)}
            role="img"
            aria-label="Kümülatif PnL çizgi grafiği"
          >
            {yer.ticks.map((t) => (
              <g key={t}>
                <line
                  className={t === 0 ? 'chart-zero' : 'chart-grid'}
                  x1={PAD.left}
                  x2={PAD.left + yer.plotWidth}
                  y1={yer.y(t)}
                  y2={yer.y(t)}
                />
                <text className="chart-tick" x={PAD.left - 8} y={yer.y(t) + 3.5}>
                  {t === 0 ? '0' : kisaFmt.format(t)}
                </text>
              </g>
            ))}

            <path className={`pnl-alan ${yer.artida ? 'poz' : 'neg'}`} d={yer.alan} />
            <path className={`pnl-cizgi ${yer.artida ? 'poz' : 'neg'}`} d={yer.cizgi} />

            {nokta && (
              <g>
                <line
                  className="chart-crosshair"
                  x1={nokta.x}
                  x2={nokta.x}
                  y1={PAD.top}
                  y2={PAD.top + yer.plotHeight}
                />
                <circle
                  className={`pnl-nokta ${yer.artida ? 'poz' : 'neg'}`}
                  cx={nokta.x}
                  cy={nokta.y}
                  r={4}
                />
              </g>
            )}
            {/* Son gün her zaman işaretli — ayın vardığı yer. */}
            <circle
              className={`pnl-nokta ${yer.artida ? 'poz' : 'neg'}`}
              cx={yer.son.x}
              cy={yer.son.y}
              r={3.5}
            />

            {Array.from({ length: gunler.length }, (_, i) => i)
              .filter((i) => i % yer.adim === 0)
              .map((i) => (
                <text
                  key={i}
                  className="chart-tick chart-tick-x"
                  x={yer.x(i)}
                  y={size.height - 6}
                >
                  {gunNo(gunler[i]!.date)}
                </text>
              ))}
          </svg>
        )}

        {kutu && vurgulu && nokta && (
          <div
            className="chart-tooltip"
            style={{
              left: Math.min(Math.max(nokta.x + 12, 8), Math.max(size.width - 168, 8)),
              top: 10,
            }}
          >
            <div className="tooltip-time">{tamTarih(vurgulu.date)}</div>
            <div className="tooltip-row">
              <span>Kümülatif</span>
              <b className={renk(nokta.v)}>{fmt(nokta.v)}</b>
            </div>
            <div className="tooltip-row">
              <span>O gün</span>
              <b className={renk(deger(vurgulu))}>{fmt(deger(vurgulu))}</b>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* ── Ortak küçük yardımcılar ──────────────────────────────────────────────── */

function fmt(v: number | null): string {
  return v == null ? '—' : tlFmt.format(v)
}
function renk(v: number | null): string {
  if (v == null || v === 0) return ''
  return v > 0 ? ' pnl-poz' : ' pnl-neg'
}

/** Veri ucu yuvarlak, sıfır ekseni ucu düz sütun (yön aşağıysa ters). */
function sutunPath(x: number, y: number, w: number, h: number, yukari: boolean): string {
  const r = Math.min(3, w / 2, h)
  if (yukari) {
    return (
      `M${x} ${y + h} L${x} ${y + r} Q${x} ${y} ${x + r} ${y} ` +
      `L${x + w - r} ${y} Q${x + w} ${y} ${x + w} ${y + r} L${x + w} ${y + h} Z`
    )
  }
  const b = y + h
  return (
    `M${x} ${y} L${x} ${b - r} Q${x} ${b} ${x + r} ${b} ` +
    `L${x + w - r} ${b} Q${x + w} ${b} ${x + w} ${b - r} L${x + w} ${y} Z`
  )
}
